import {
  JaneGoldTag,
  SpecialTag,
  getSpecialTagLabel,
  productPhotoMetadata,
} from '@iheartjane/menu';
import {
  Flex,
  Image,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { titleize } from '@iheartjane/utils';

import { useShouldShowGold } from '@jane/shared/hooks';
/* eslint-disable-next-line no-restricted-imports */
import type { MenuProduct } from '@jane/shared/models';
import { generateCustomLabel } from '@jane/shared/util';

import { Category } from '../../legacyProductCard.utils';
import { useProductCardContext } from '../../productCardProvider';
import { potencyInfo } from '../../productPotency/productPotency';
import { FiveStarRating } from '../../starRatings';
import { useProductReviewsEnabled } from '../utils/useProductReviewsEnabled';
import { StyledCardContent } from './listViewProductCard.styles';

export const ProductInfoCardContent = () => {
  const {
    appMode,
    currentSpecial,
    janeGoldLabel,
    menuProduct,
    store,
    userLocation,
  } = useProductCardContext();
  const { brand, category, kind, brand_subtype, name } =
    menuProduct as MenuProduct;

  const isVerySmallMobile = useMobileMediaQuery({ width: 'max', size: 'sm' });
  const smallerThanVerticalTablet = useMobileMediaQuery({});
  const shouldShowGold = useShouldShowGold({ store });

  const specialTagLabel =
    store &&
    menuProduct &&
    getSpecialTagLabel({
      product: menuProduct,
      store,
      shortenLabel: true,
    });

  const productLineage = generateCustomLabel({
    appMode,
    store,
    attribute: category,
    isLineageLabel: true,
    fallback: titleize(category),
  });

  const specialApplies = specialTagLabel && currentSpecial;

  const photoMetadata = productPhotoMetadata(menuProduct);

  const reviewsEnabled = useProductReviewsEnabled(appMode, userLocation, store);

  const janeGoldTagLabel = shouldShowGold ? janeGoldLabel : false;

  return (
    <StyledCardContent>
      <Flex
        flexDirection={
          isVerySmallMobile
            ? 'column'
            : smallerThanVerticalTablet
              ? 'row-reverse'
              : 'row'
        }
        alignItems={isVerySmallMobile ? 'center' : 'flex-start'}
      >
        <Flex position="relative">
          <Image
            src={photoMetadata.url}
            altText={menuProduct?.name || ''}
            border
            borderRadius="rounded"
            height="96px"
            width="96px"
            loading="lazy"
            responsive
            sizes="thumbnail"
          />
          <Flex position="absolute" top={8} right={8}>
            {specialApplies && <SpecialTag label={specialTagLabel} />}
          </Flex>
        </Flex>
        <Flex
          pl={smallerThanVerticalTablet ? 0 : 16}
          flexDirection="column"
          width="100%"
        >
          <Flex flexDirection={'column'}>
            {category && (
              <Category
                title={category || ''}
                category={category}
                editingMode={false}
              >
                {productLineage}
              </Category>
            )}
            <Typography variant="h2-semibold" branded>
              {name}
            </Typography>

            <Flex flexWrap="wrap">
              {!!brand && <Typography color="purple">{brand}</Typography>}
              {janeGoldTagLabel && (
                <JaneGoldTag my={2} ml={4} label={janeGoldTagLabel} />
              )}
            </Flex>

            <Flex flexWrap="wrap">
              <Typography>
                {brand_subtype ? brand_subtype : titleize(kind)}
              </Typography>
              <Typography color="text-light" ml={16}>
                {menuProduct && potencyInfo(menuProduct)}
              </Typography>
            </Flex>
          </Flex>

          {reviewsEnabled && menuProduct && menuProduct.aggregate_rating ? (
            <Flex alignItems="center">
              <FiveStarRating rating={menuProduct.aggregate_rating} />

              <Typography mx={4}>{menuProduct.aggregate_rating}</Typography>
              <Typography color="text-light">
                ({menuProduct.review_count})
              </Typography>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </StyledCardContent>
  );
};
